import React from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import { 
  ShoppingBag, 
  Package, 
  Clock, 
  Headphones, 
  Building, 
  BookOpen, 
  Settings, 
  FileText,
  ShoppingCart,
  Truck
} from 'lucide-react';

const SearchFilter = styled.aside`
  position: absolute;
  top: 0;
  left: 0;
  width: 250px;
  height: 100%;
  padding: 30px 20px;
  background: #fafafa;
  border-right: 1px solid #e5e7eb;
  overflow-y: auto;
`;

const CategoryTitle = styled.strong`
  display: block;
  margin-bottom: 12px;
  font-size: 14px;
  color: #111;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CategoryFilter = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 30px;
`;

const ItemFilterButton = styled.button`
  padding: 8px 12px;
  border: none;
  background: ${props => props.active ? '#000' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#555'};
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  border-radius: 4px;
  transition: all 0.15s ease;
  font-weight: ${props => props.active ? '600' : '400'};

  &:hover {
    background-color: ${props => props.active ? '#333' : '#e5e7eb'};
    color: ${props => props.active ? '#fff' : '#000'};
    transform: translateX(2px);
  }

  &:active {
    background-color: #d1d5db;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0,0,0,0.1);
  }
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 15px;
  border: none;
  background: ${props => props.active ? '#111827' : 'transparent'};
  color: ${props => props.active ? '#fff' : '#374151'};
  cursor: pointer;
  text-align: left;
  font-size: 14px;
  border-radius: 8px;
  transition: all 0.2s ease;
  font-weight: ${props => props.active ? '600' : '500'};
  width: 100%;
  margin-bottom: 4px;

  &:hover {
    background-color: ${props => props.active ? '#1f2937' : '#f3f4f6'};
    color: ${props => props.active ? '#fff' : '#111827'};
    transform: translateX(2px);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 20px 0;
`;

const SideNav = ({ onFilter, selectedCategory }) => {
  const history = useHistory();
  const location = useLocation();
  const currentPath = location.pathname;

  const handleNavigation = (path) => {
    history.push(path);
  };

  const isShopPage = currentPath === '/' || currentPath === '/Home' || currentPath === '/Shop';
  const isAdminPage = currentPath.startsWith('/admin');

  if (isAdminPage) {
    return (
      <SearchFilter>
        <CategoryTitle>관리자 메뉴</CategoryTitle>
        <CategoryFilter>
          <MenuButton 
            active={currentPath === '/admin'}
            onClick={() => handleNavigation('/admin')}
          >
            <Settings size={16} />
            대시보드
          </MenuButton>
          <MenuButton 
            active={currentPath === '/admin/members'}
            onClick={() => handleNavigation('/admin/members')}
          >
            <Package size={16} />
            회원관리
          </MenuButton>
          <MenuButton 
            active={currentPath === '/admin/management'}
            onClick={() => handleNavigation('/admin/management')}
          >
            <ShoppingBag size={16} />
            통합관리
          </MenuButton>
        </CategoryFilter>
      </SearchFilter>
    );
  }

  if (isShopPage) {
    return (
      <SearchFilter>
        <CategoryTitle>메뉴</CategoryTitle>
        <CategoryFilter>
          <MenuButton 
            active={currentPath === '/' || currentPath === '/Home'}
            onClick={() => handleNavigation('/')}
          >
            <ShoppingBag size={16} />
            전체상품
          </MenuButton>
          <MenuButton 
            active={currentPath === '/delivery-tracking'}
            onClick={() => handleNavigation('/delivery-tracking')}
          >
            <Truck size={16} />
            배송추적
          </MenuButton>
          <MenuButton 
            active={currentPath === '/quote-request'}
            onClick={() => handleNavigation('/quote-request')}
          >
            <FileText size={16} />
            견적의뢰
          </MenuButton>
          <MenuButton 
            active={currentPath === '/order-history'}
            onClick={() => handleNavigation('/order-history')}
          >
            <Clock size={16} />
            주문내역
          </MenuButton>
          <MenuButton 
            active={currentPath.startsWith('/support')}
            onClick={() => handleNavigation('/support')}
          >
            <Headphones size={16} />
            고객센터
          </MenuButton>
          <MenuButton 
            active={currentPath === '/company-info'}
            onClick={() => handleNavigation('/company-info')}
          >
            <Building size={16} />
            회사소개
          </MenuButton>
          <MenuButton 
            active={currentPath === '/system-guide'}
            onClick={() => handleNavigation('/system-guide')}
          >
            <BookOpen size={16} />
            시스템가이드
          </MenuButton>
        </CategoryFilter>

        <Divider />

        <CategoryTitle>카테고리</CategoryTitle>
        <CategoryFilter>
          <ItemFilterButton 
            active={selectedCategory === "전체"}
            onClick={() => onFilter && onFilter("전체")}
          >
            전체
          </ItemFilterButton>
          <ItemFilterButton 
            active={selectedCategory === "의류"}
            onClick={() => onFilter && onFilter("의류")}
          >
            의류
          </ItemFilterButton>
          <ItemFilterButton 
            active={selectedCategory === "신발"}
            onClick={() => onFilter && onFilter("신발")}
          >
            신발
          </ItemFilterButton>
          <ItemFilterButton 
            active={selectedCategory === "가방"}
            onClick={() => onFilter && onFilter("가방")}
          >
            가방
          </ItemFilterButton>
          <ItemFilterButton 
            active={selectedCategory === "전자제품"}
            onClick={() => onFilter && onFilter("전자제품")}
          >
            전자제품
          </ItemFilterButton>
        </CategoryFilter>

        <CategoryTitle>브랜드</CategoryTitle>
        <CategoryFilter>
          <ItemFilterButton>전체</ItemFilterButton>
          <ItemFilterButton>NIKE</ItemFilterButton>
          <ItemFilterButton>ADIDAS</ItemFilterButton>
          <ItemFilterButton>APPLE</ItemFilterButton>
          <ItemFilterButton>SAMSUNG</ItemFilterButton>
        </CategoryFilter>
        
        <CategoryTitle>가격대</CategoryTitle>
        <CategoryFilter>
          <ItemFilterButton>전체</ItemFilterButton>
          <ItemFilterButton>10만원 이하</ItemFilterButton>
          <ItemFilterButton>10만원~20만원</ItemFilterButton>
          <ItemFilterButton>20만원~50만원</ItemFilterButton>
          <ItemFilterButton>50만원 이상</ItemFilterButton>
        </CategoryFilter>
      </SearchFilter>
    );
  }

  // 기타 페이지에서는 기본 메뉴만 표시
  return (
    <SearchFilter>
      <CategoryTitle>메뉴</CategoryTitle>
      <CategoryFilter>
        <MenuButton 
          active={currentPath === '/' || currentPath === '/Home'}
          onClick={() => handleNavigation('/')}
        >
          <ShoppingBag size={16} />
          전체상품
        </MenuButton>
        <MenuButton 
          active={currentPath === '/delivery-tracking'}
          onClick={() => handleNavigation('/delivery-tracking')}
        >
          <Truck size={16} />
          배송추적
        </MenuButton>
        <MenuButton 
          active={currentPath === '/quote-request'}
          onClick={() => handleNavigation('/quote-request')}
        >
          <FileText size={16} />
          견적의뢰
        </MenuButton>
        <MenuButton 
          active={currentPath === '/order-history'}
          onClick={() => handleNavigation('/order-history')}
        >
          <Clock size={16} />
          주문내역
        </MenuButton>
        <MenuButton 
          active={currentPath === '/support'}
          onClick={() => handleNavigation('/support')}
        >
          <Headphones size={16} />
          고객센터
        </MenuButton>
        <MenuButton 
          active={currentPath === '/company-info'}
          onClick={() => handleNavigation('/company-info')}
        >
          <Building size={16} />
          회사소개
        </MenuButton>
        <MenuButton 
          active={currentPath === '/system-guide'}
          onClick={() => handleNavigation('/system-guide')}
        >
          <BookOpen size={16} />
          시스템가이드
        </MenuButton>
        <MenuButton 
          active={currentPath.startsWith('/admin')}
          onClick={() => handleNavigation('/admin')}
        >
          <Settings size={16} />
          관리자
        </MenuButton>
      </CategoryFilter>
    </SearchFilter>
  );
};

export default SideNav;